/**
 * EditActionHandlerTask - таск для відкриття карточки ресурсу в режимі редагування
 *
 * Використовує нову систему карточок через OpenResourceCardTask.
 * Карточка відкривається в Drawer через ResourceCardRenderer.
 *
 * Завантаження даних ресурсу виконується автоматично в компоненті ResourceDetail
 * при передачі itemId та mode='edit'. ResourceDetail викличе resourceClient.get()
 * та обробить відповідь, включаючи події onResponseRequest та onResponseEditRequest.
 *
 * Підтримує подію onBeforeSendEditRequest для можливості скасувати відкриття.
 *
 * @param {Object} params - Параметри
 * @param {Object} params.item - Елемент для редагування (повинен містити id)
 * @param {Object} params.crudContext - Контекст CRUD ресурсу
 */

import type { IOpenResourceCardResult } from '@/Core/Tasks/Resources/OpenResourceCardTask'
import { OpenResourceCardTask } from '@/Core/Tasks/Resources/OpenResourceCardTask'
import { navigationService } from '@/Core/Services/NavigationService'

/**
 * Параметри для EditActionHandlerTask
 */
interface IEditActionHandlerParams {
    /** Елемент для редагування */
    item: {
        /** ID елемента */
        id: string | number
        [key: string]: any
    }
    /** Контекст CRUD ресурсу */
    crudContext: any // CrudResourceStore з mobx
    /** Додаткові параметри для OpenResourceCardTask (дозволяє перезаписати onClose, drawer, displayMode тощо) */
    openResourceCardParams?: Partial<Parameters<typeof OpenResourceCardTask>[0]>
}

export const EditActionHandlerTask = async ({
    item,
    crudContext,
    openResourceCardParams,
}: IEditActionHandlerParams): Promise<IOpenResourceCardResult | undefined> => {
    // Отримуємо назву поля первинного ключа з конфігурації ресурсу
    const primaryFieldCode = crudContext.resource?.primaryFieldCode || 'uuid'
    const primaryFieldValue = item?.[primaryFieldCode]

    // Перевіряємо наявність значення первинного ключа
    if (!primaryFieldValue) {
        console.error(`EditActionHandlerTask: item.${primaryFieldCode} is required`)
        return
    }

    // Викликаємо подію перед відкриттям для можливості скасування
    const eventResults = await crudContext.eventManager.emitAsync('onBeforeSendEditRequest', {
        item,
        crudContext,
    })

    // Якщо хоча б один обробник повернув false - скасовуємо відкриття
    if (eventResults && eventResults.some((result: any) => result === false)) {
        return
    }

    // Визначаємо режим відображення:
    // 1. Пріоритет - openResourceCardParams.displayMode
    // 2. modeConfig.edit.displayMode
    // 3. currentDetailMode з crudContext (встановлюється в ResourceDetail)
    // 4. Загальний detailConfig.mode з конфігурації ресурсу
    // 5. За замовчуванням - drawer
    const displayMode = 
        openResourceCardParams?.displayMode ||
        crudContext.resource?.detailConfig?.modeConfig?.edit?.displayMode ||
        crudContext.currentDetailMode || 
        crudContext.resource?.detailConfig?.mode || 
        'drawer'

    // Визначаємо чи викликається з вже відкритої карточки
    // Якщо formContext існує і має дані - це переключення режиму всередині карточки
    const isModeSwitchInCard = !!(crudContext.formContext && crudContext.formContext.values)

    // Для page режиму при переключенні всередині карточки використовуємо навігацію
    if (displayMode === 'page' && isModeSwitchInCard) {
        const url = crudContext.resource?.editPageUrl
            ? `${crudContext.resource.editPageUrl}/${primaryFieldValue}`
            : crudContext.resource?.pageUrl
              ? `${crudContext.resource.pageUrl}/${primaryFieldValue}/edit`
              : `/${crudContext.resource.key}/${primaryFieldValue}/edit`

        if (navigationService.isReady()) {
            navigationService.navigateTo(url)
        } else {
            console.warn('NavigationService не готовий, використовується fallback')
            if (typeof window !== 'undefined') {
                window.location.href = url
            }
        }

        return {
            cardId: 'page-navigation',
            url,
            level: 0,
            zIndex: 0,
            close: () => {},
        }
    }

    // Визначаємо ефективний list context для перезавантаження при закритті:
    // - якщо викликано з list контексту — використовуємо сам crudContext
    // - якщо викликано з detail контексту (переключення режиму) — використовуємо збережений listCrudContext
    const listCrudContextForReload = crudContext.contextType === 'list' ? crudContext : crudContext.listCrudContext

    // Дефолтні параметри
    const defaultParams = {
        resource: crudContext.resource,
        primaryFieldValue: primaryFieldValue,
        mode: 'edit' as const,
        displayMode: displayMode,
        // Передаємо listCrudContext для перезавантаження списку після збереження
        listCrudContext: listCrudContextForReload,
        // Для drawer/modal: якщо це переключення всередині карточки - замінюємо поточну
        replace: isModeSwitchInCard && (displayMode === 'drawer' || displayMode === 'modal'),
        drawer: {
            title: crudContext.drawer?.title || crudContext.resource?.title,
            width: crudContext.drawer?.width || 500,
        },
        onClose: () => {
            // Перезавантажуємо список при закритті — зміни можуть бути внесені не тільки через кнопку збереження
            // Використовуємо listCrudContext напряму, щоб уникнути відкриття картки через ReloadActionHandlerTask
            if (listCrudContextForReload?.listContext?.actions?.load) {
                listCrudContextForReload.listContext.actions.load(true)
            }

            // Викликаємо події при закритті
            crudContext.eventManager.emit('onCloseDrawer', {
                crudContext,
            })
        },
        onModeChange: (mode: string) => {
            // Обробка зміни режиму (наприклад, з edit на view)
            crudContext.eventManager.emit('onModeChange', {
                mode,
                crudContext,
            })
        },
    }

    // Об'єднуємо дефолтні параметри з переданими через openResourceCardParams
    // openResourceCardParams має вищий пріоритет
    const result = OpenResourceCardTask({
        ...defaultParams,
        ...openResourceCardParams,
        // Drawer об'єднуємо окремо для можливості часткової перезапису
        drawer: {
            ...defaultParams.drawer,
            ...openResourceCardParams?.drawer,
        },
    })

    // Викликаємо подію після відкриття
    crudContext.eventManager.emit('onOpenEditCard', {
        item,
        cardId: result.cardId,
        crudContext,
    })

    return result
}
